import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { ResearchProject, ProjectStatus, KoboAccount } from '../types';
import { apiService } from '../services/api';

const ProjectsPage: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ResearchProject[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<ResearchProject[]>([]);
  const [koboAccounts, setKoboAccounts] = useState<KoboAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [koboAccountsLoading, setKoboAccountsLoading] = useState(true);
  const [globalFilter, setGlobalFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | ''>('');
  const [koboAccountFilter, setKoboAccountFilter] = useState<string>('');
  const toast = useRef<Toast>(null);

  const statusOptions = [
    { label: 'All Statuses', value: '' },
    { label: 'Draft', value: 'draft' },
    { label: 'TOR Uploaded', value: 'tor_uploaded' },
    { label: 'Indicators Extracted', value: 'indicators_extracted' },
    { label: 'Questionnaire Designed', value: 'questionnaire_designed' },
    { label: 'Kobo Deployed', value: 'kobo_deployed' },
    { label: 'Data Collection', value: 'data_collection' },
    { label: 'Analysis', value: 'analysis' },
    { label: 'Complete', value: 'complete' },
  ];

  useEffect(() => { 
    loadProjects(); 
    loadKoboAccounts();
  }, []);
  useEffect(() => { filterProjects(); }, [projects, globalFilter, statusFilter, koboAccountFilter]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await apiService.getProjects();
      if (response.success && response.data) setProjects(response.data);
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load projects' });
    } finally {
      setLoading(false);
    }
  };

  const loadKoboAccounts = async () => {
    try {
      setKoboAccountsLoading(true);
      const response = await apiService.getUserProfile();
      if (response.success && response.data) {
        setKoboAccounts(response.data.kobo_accounts || []);
      }
    } catch (error) {
      console.error('Failed to load KoboToolbox accounts:', error);
      toast.current?.show({ 
        severity: 'warn', 
        summary: 'Warning', 
        detail: 'Could not load KoboToolbox accounts. Account names may not display correctly.',
        life: 5000
      });
    } finally {
      setKoboAccountsLoading(false);
    }
  };

  const filterProjects = () => {
    let filtered = [...projects];
    if (statusFilter) filtered = filtered.filter(p => p.status === statusFilter);
    if (koboAccountFilter) filtered = filtered.filter(p => p.koboAccountId === koboAccountFilter);
    if (globalFilter) {
      const term = globalFilter.toLowerCase();
      filtered = filtered.filter(p => p.title.toLowerCase().includes(term) || p.description?.toLowerCase().includes(term));
    }
    setFilteredProjects(filtered);
  };

  const deleteProject = async (projectId: string) => {
    try {
      const response = await apiService.deleteProject(projectId);
      if (response.success) {
        setProjects(prev => prev.filter(p => p.projectId !== projectId));
        toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Project deleted' });
      }
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to delete project' });
    }
  };

  const confirmDelete = (project: ResearchProject) => {
    confirmDialog({
      message: `Delete "${project.title}"? This cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      accept: () => deleteProject(project.projectId),
    });
  };

  const getStatusSeverity = (status: string) => {
    switch (status) {
      case 'complete': return 'success';
      case 'data_collection': return 'info';
      case 'kobo_deployed': return 'warning';
      case 'draft': return 'secondary';
      default: return 'info';
    }
  };

  const statusBodyTemplate = (rowData: ResearchProject) => (
    <Tag value={rowData.status.replace('_', ' ').toUpperCase()} severity={getStatusSeverity(rowData.status)} />
  );

  const actionBodyTemplate = (rowData: ResearchProject) => (
    <div style={{ display: 'flex', gap: '0.5rem' }}>
      <Button icon="pi pi-eye" size="small" text onClick={() => navigate(`/app/projects/${rowData.projectId}`)} tooltip="View" />
      <Button icon="pi pi-pencil" size="small" text onClick={() => navigate(`/app/projects/${rowData.projectId}/edit`)} tooltip="Edit" />
      <Button icon="pi pi-trash" size="small" text severity="danger" onClick={() => confirmDelete(rowData)} tooltip="Delete" />
    </div>
  );

  const koboAccountBodyTemplate = (rowData: ResearchProject) => {
    // Handle case where koboAccounts haven't loaded yet
    if (koboAccountsLoading) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <i className="pi pi-spin pi-spinner" style={{ fontSize: '0.875rem', color: '#6b7280' }}></i>
          <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Loading...</span>
        </div>
      );
    }
    
    // No kobo account assigned to this project
    if (!rowData.koboAccountId) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <i className="pi pi-minus-circle" style={{ color: '#9ca3af' }}></i>
          <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Not Assigned</span>
        </div>
      );
    }
    
    const account = koboAccounts.find(acc => acc.account_id === rowData.koboAccountId);
    
    if (account) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <i className="pi pi-cloud" style={{ color: account.is_active ? '#10b981' : '#ef4444' }}></i>
          <span>{account.account_name}</span>
          {!account.is_active && (
            <Tag value="Inactive" severity="danger" style={{ fontSize: '0.625rem', padding: '0.125rem 0.25rem' }} />
          )}
        </div>
      );
    }
    
    // Account ID exists but not found in user's accounts (might be deleted or access revoked)
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <i className="pi pi-exclamation-triangle" style={{ color: '#f59e0b' }}></i>
        <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Account Not Found</span>
        <Tag value="Check Settings" severity="warning" style={{ fontSize: '0.625rem', padding: '0.125rem 0.25rem' }} />
      </div>
    );
  };

  const dateBodyTemplate = (rowData: ResearchProject) => new Date(rowData.createdAt).toLocaleDateString();

  const koboAccountOptions = [
    { label: 'All Accounts', value: '' },
    ...koboAccounts.map(account => ({
      label: account.account_name,
      value: account.account_id
    }))
  ];

  const cardStyle: React.CSSProperties = { backgroundColor: 'white', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' };

  return (
    <div>
      {/* Header Section */}
      <div style={{ padding: '2rem', backgroundColor: 'white', borderBottom: '1px solid #e5e7eb' }}>
        <h1 style={{ fontSize: '1.5rem', margin: 0, color: '#1f2937' }}>Research Projects</h1>
      </div>

      {/* Main Content Section */}
      <div style={{ padding: '2rem', backgroundColor: '#f8f9fa', minHeight: 'calc(100vh - 140px)' }}>
        <div style={cardStyle}>
          {/* Filters */}
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            <span className="p-input-icon-left">
              <i className="pi pi-search" />
              <InputText value={globalFilter} onChange={(e) => setGlobalFilter(e.target.value)} placeholder="Search projects..." style={{ width: '300px' }} />
            </span>
            <Dropdown value={statusFilter} onChange={(e) => setStatusFilter(e.value)} options={statusOptions} placeholder="Filter by status" style={{ width: '200px' }} />
            <Dropdown 
              value={koboAccountFilter} 
              onChange={(e) => setKoboAccountFilter(e.value)} 
              options={koboAccountOptions} 
              placeholder="Filter by KoboToolbox account" 
              style={{ width: '250px' }} 
            />
            <div style={{ marginLeft: 'auto' }}>
              <Button label="New Project" icon="pi pi-plus" onClick={() => navigate('/app/projects/new')} />
            </div>
          </div>

          <DataTable
            value={filteredProjects}
            loading={loading}
            responsiveLayout="scroll"
            paginator
            rows={10}
            rowsPerPageOptions={[5, 10, 25, 50]}
            className="p-datatable-sm"
            emptyMessage="No projects found"
          >
            <Column field="title" header="Project Title" sortable />
            <Column field="description" header="Description" />
            <Column field="status" header="Status" body={statusBodyTemplate} sortable />
            <Column header="KoboToolbox Account" body={koboAccountBodyTemplate} sortable />
            <Column field="createdAt" header="Created" body={dateBodyTemplate} sortable />
            <Column body={actionBodyTemplate} header="Actions" style={{ width: '10rem' }} />
          </DataTable>
        </div>
      </div>

      <Toast ref={toast} />
      <ConfirmDialog />
    </div>
  );
};

export default ProjectsPage;